import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, UserCheck, UserX } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';

const API_URL = getApiUrl();

export default function BlogAuthorsPage() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [form, setForm] = useState({ full_name: '', credential: '', avatar_url: '', bio: '' });
  const [saving, setSaving] = useState(false);

  const fetchAuthors = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/admin/blog/authors`);
      setAuthors(data.authors || []);
    } catch (err) {
      toast.error('Failed to load authors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAuthors(); }, []);

  const openCreate = () => {
    setEditingAuthor(null);
    setForm({ full_name: '', credential: '', avatar_url: '', bio: '' });
    setModalOpen(true);
  };

  const openEdit = (author) => {
    setEditingAuthor(author);
    setForm({
      full_name: author.full_name || '',
      credential: author.credential || '',
      avatar_url: author.avatar_url || '',
      bio: author.bio || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingAuthor) {
        await axios.put(`${API_URL}/api/admin/blog/authors/${editingAuthor.slug}`, form);
        toast.success('Author updated');
      } else {
        await axios.post(`${API_URL}/api/admin/blog/authors`, form);
        toast.success('Author created');
      }
      setModalOpen(false);
      fetchAuthors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save author');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (author) => {
    try {
      if (author.is_active) {
        await axios.delete(`${API_URL}/api/admin/blog/authors/${author.slug}`);
        toast.success('Author deactivated');
      } else {
        await axios.put(`${API_URL}/api/admin/blog/authors/${author.slug}`, { is_active: true });
        toast.success('Author reactivated');
      }
      fetchAuthors();
    } catch (err) {
      toast.error('Failed to update author');
    }
  };

  return (
    <div className="space-y-6" data-testid="blog-authors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="blog-authors-title">Blog Authors</h1>
          <p className="text-sm text-slate-500 mt-1">Manage expert profiles for blog articles</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="create-author-btn">
          <Plus className="w-4 h-4" /> Add Author
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : authors.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <p className="font-medium">No authors yet</p>
            <p className="text-sm mt-1">Add your first blog author.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="blog-authors-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 font-medium text-slate-600">Author</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Credential</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Slug</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {authors.map((author) => (
                <tr key={author.slug} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`author-row-${author.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {author.avatar_url ? (
                        <img src={author.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                          {author.full_name?.charAt(0)}
                        </div>
                      )}
                      <span className="font-medium text-slate-900">{author.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{author.credential || '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs font-mono hidden lg:table-cell">{author.slug}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={author.is_active ? 'default' : 'secondary'} className={author.is_active ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-slate-100 text-slate-500'}>
                      {author.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(author)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit" data-testid={`edit-author-${author.slug}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </button>
                      <button
                        onClick={() => toggleActive(author)}
                        className="p-1.5 hover:bg-slate-100 rounded"
                        title={author.is_active ? 'Deactivate' : 'Reactivate'}
                        data-testid={`toggle-author-${author.slug}`}
                      >
                        {author.is_active
                          ? <UserX className="w-4 h-4 text-red-400" />
                          : <UserCheck className="w-4 h-4 text-green-500" />
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="author-modal">
          <DialogHeader>
            <DialogTitle>{editingAuthor ? 'Edit Author' : 'New Author'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Dr. Meera Sharma"
                data-testid="author-name-input"
              />
            </div>
            <div>
              <Label>Credential</Label>
              <Input
                value={form.credential}
                onChange={(e) => setForm({ ...form, credential: e.target.value })}
                placeholder="Prenatal Yoga Specialist, 8+ yrs"
                data-testid="author-credential-input"
              />
            </div>
            <div>
              <Label>Avatar URL</Label>
              <Input
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                placeholder="https://..."
                data-testid="author-avatar-input"
              />
            </div>
            <div>
              <Label>Bio</Label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="1-2 sentence bio"
                rows={3}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="author-bio-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-author-btn">
              {saving ? 'Saving...' : editingAuthor ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
